import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { authApi } from '../api/auth';
import { OfflineBanner } from '../components/OfflineBanner';

interface AcceptInviteForm {
  name: string;
  surname: string;
  dateOfBirth: Dayjs | null;
  password: string;
  confirmPassword: string;
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteForm>({
    defaultValues: { name: '', surname: '', dateOfBirth: null, password: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="error">
          Token di invito mancante. Richiedi un nuovo invito.
        </Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Account attivato!
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Puoi ora accedere con le tue credenziali.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Vai al login
        </Button>
      </Container>
    );
  }

  const onSubmit = async (data: AcceptInviteForm) => {
    setServerError(null);
    try {
      await authApi.acceptInvite({
        token,
        password: data.password,
        name: data.name.trim(),
        surname: data.surname.trim(),
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.format('YYYY-MM-DD') : '',
      });
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Invito non valido o scaduto.';
      setServerError(msg);
    }
  };

  return (
    <>
      <OfflineBanner />
      <Container
        maxWidth="xs"
        sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', py: 4 }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 3 }}>
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" fontWeight={700} color="primary">
              Attiva il tuo account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scegli una password per completare la registrazione.
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Nome *"
              size="small"
              {...register('name', { required: 'Nome obbligatorio' })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            <TextField
              label="Cognome *"
              size="small"
              {...register('surname', { required: 'Cognome obbligatorio' })}
              error={!!errors.surname}
              helperText={errors.surname?.message}
            />

            <Controller
              name="dateOfBirth"
              control={control}
              rules={{ required: 'Data di nascita obbligatoria' }}
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Data di nascita *"
                  value={field.value}
                  onChange={field.onChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                />
              )}
            />

            <TextField
              label="Password"
              type="password"
              size="small"
              autoComplete="new-password"
              {...register('password', {
                required: 'Password obbligatoria',
                minLength: { value: 8, message: 'Minimo 8 caratteri' },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />

            <TextField
              label="Conferma password"
              type="password"
              size="small"
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: 'Conferma la password',
                validate: (val) =>
                  val === watch('password') || 'Le password non coincidono',
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />

            {serverError && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                {serverError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {isSubmitting ? 'Attivazione…' : 'Attiva account'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
